import PropTypes from 'prop-types';
import App from 'next/app';
import Head from 'next/head';
import { AnalyticsProvider } from 'use-analytics';
import { connect } from 'react-redux';
import wrapper from '../store';
import '../styles/globals.css';
import analytics from '../utils/analytics';
import TwThemeProvider from '../HOCs/TwThemeProvider';
import AuthProvider from '../HOCs/AuthProvider';
import TENANT_TITLES from '../constants/TenantTitles';
import { ICONS_BASE } from '../constants';
import BrowserEventsTracker from '../components/BrowserEventsTracker';

const DEFAULT_TITLE = 'TravelWits';
const TRAVEL_WITS_SUFFIX = '.travelwits.com';
const DEFAULT_FAVICON = `${ICONS_BASE}/agent.favicon.ico`;

function normalizeHost(host = '') {
  return String(host).split(':')[0].toLowerCase();
}

function parseHost(rawHost = '') {
  const host = normalizeHost(rawHost);
  const isTravelWitsDomain = host.endsWith(TRAVEL_WITS_SUFFIX);

  const tenant = isTravelWitsDomain
    ? host.replace(TRAVEL_WITS_SUFFIX, '')
    : (host.split('.')[0] || '');

  return { host, tenant, isTravelWitsDomain };
}

function resolveTitleByHost(host, tenant) {
  if (!host) return DEFAULT_TITLE;

  // TENANT_TITLES is [host => title]
  if (TENANT_TITLES[host]) return TENANT_TITLES[host];

  // fallback (optional): humanize tenant, else default
  if (!tenant) return DEFAULT_TITLE;
  return `${tenant.charAt(0).toUpperCase()}${tenant.slice(1)}`;
}

function resolveFavicon(tenant, isTravelWitsDomain) {
  // tenant favicon only for *.travelwits.com
  if (!tenant || !isTravelWitsDomain) return DEFAULT_FAVICON;
  return `${ICONS_BASE}/${tenant}.favicon.ico`;
}

class MyApp extends App {
  static async getInitialProps(appCtx) {
    const appProps = await App.getInitialProps(appCtx);

    const rawHost = appCtx?.ctx?.req?.headers?.host
    || (typeof window !== 'undefined' ? window.location.host : '');
    const { host, tenant, isTravelWitsDomain } = parseHost(rawHost);
    const siteTitleSSR = resolveTitleByHost(host, tenant);
    const faviconHref = resolveFavicon(tenant, isTravelWitsDomain);

    return {
      ...appProps,
      siteTitleSSR,
      faviconHref,
    };
  }

  render() {
    const {
      Component,
      pageProps,
      siteTitle, // redux
      siteTitleSSR, // SSR fallback
      faviconHref,
    } = this.props;

    const title = siteTitle || siteTitleSSR || DEFAULT_TITLE;
    return (
      <>
        <Head>
          {/* Add your meta tags, styles, scripts, and other head elements here */}
          <title>{title}</title>
          {/* Icons (IMPORTANT: must have href on first render) */}
          <link id="favicon" rel="icon" href={faviconHref} />
          {/* Adding fonts */}
          <link rel="stylesheet" href="/fonts/fonts.css" />
        </Head>
        <AnalyticsProvider instance={analytics}>
          <BrowserEventsTracker />
          <AuthProvider>
            <TwThemeProvider>

              {/* eslint-disable-next-line react/jsx-props-no-spreading */}
              <Component {...pageProps} />
            </TwThemeProvider>
          </AuthProvider>
        </AnalyticsProvider>
      </>
    );
  }
}

MyApp.propTypes = {
  Component: PropTypes.oneOfType([PropTypes.array, PropTypes.object, PropTypes.func]).isRequired,
  pageProps: PropTypes.shape(),
};

MyApp.defaultProps = {
  pageProps: {},
};

const mapStateToProps = (state) => ({
  siteTitle: state.user.travelAgency && state.user.travelAgency.settings
    && state.user.travelAgency.settings.siteTitle,
});

export default wrapper.withRedux(connect(mapStateToProps)(MyApp));
